import { useState } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import {
  listDestinations,
  createDestination,
  updateDestination,
  deleteDestination,
  type Destination,
} from "../api/config";

const PAGE_SIZE = 50;

function DestinationEditor({
  destination,
  onClose,
  onSaved,
}: {
  destination: Destination | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(destination?.name ?? "");
  const [prefix, setPrefix] = useState(destination?.prefix ?? "");
  const [ocn, setOcn] = useState(destination?.ocn ?? "");

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name,
        prefix,
        ocn: ocn || null,
        country_id: undefined,
        timezone_id: undefined,
      };
      return destination
        ? updateDestination(destination.destination_id, body)
        : createDestination(body).then(() => undefined);
    },
    onSuccess: onSaved,
  });

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-10 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md space-y-3">
        <h2 className="text-lg font-bold">{destination ? "Edit" : "New"} destination</h2>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Name</label>
          <input
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Prefix</label>
          <input
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
            value={prefix}
            onChange={(e) => setPrefix(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">OCN (optional)</label>
          <input
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={ocn}
            onChange={(e) => setOcn(e.target.value)}
          />
        </div>
        {save.isError && <p className="text-xs text-red-600">{(save.error as Error).message}</p>}
        <div className="flex gap-2 pt-2">
          <button
            disabled={!name || !prefix || save.isPending}
            onClick={() => save.mutate()}
            className="flex-1 bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm disabled:opacity-50"
          >
            {save.isPending ? "Saving…" : "Save"}
          </button>
          <button onClick={onClose} className="flex-1 bg-gray-100 hover:bg-gray-200 rounded py-2 text-sm">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export function DestinationsPage() {
  const [page, setPage] = useState(1);
  const qc = useQueryClient();
  const [editor, setEditor] = useState<Destination | "new" | null>(null);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ["destinations", page],
    queryFn: () => listDestinations(page, PAGE_SIZE),
    placeholderData: keepPreviousData,
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteDestination(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["destinations"] }),
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / (data.per_page || PAGE_SIZE))) : 1;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Destinations</h1>
        <button
          onClick={() => setEditor("new")}
          className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-2 text-sm"
        >
          New destination
        </button>
      </div>

      {isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : isError ? (
        <p className="text-red-600">{(error as Error).message}</p>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
            {isFetching ? "Loading…" : `${data?.total.toLocaleString() ?? 0} destinations`}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Prefix</th>
                <th className="px-4 py-2">OCN</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(data?.data ?? []).length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-3 text-gray-500">
                    No destinations.
                  </td>
                </tr>
              ) : (
                (data?.data ?? []).map((d) => (
                  <tr key={d.destination_id}>
                    <td className="px-4 py-2 font-medium">{d.name}</td>
                    <td className="px-4 py-2 font-mono">{d.prefix}</td>
                    <td className="px-4 py-2 text-gray-600">{d.ocn || "—"}</td>
                    <td className="px-4 py-2 text-right space-x-3">
                      <button onClick={() => setEditor(d)} className="text-brand hover:underline">
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`Delete destination "${d.name}"?`)) remove.mutate(d.destination_id);
                        }}
                        className="text-red-600 hover:underline"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100 text-sm">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-gray-500">
              Page {page} of {totalPages}
            </span>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {editor && (
        <DestinationEditor
          destination={editor === "new" ? null : editor}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            qc.invalidateQueries({ queryKey: ["destinations"] });
          }}
        />
      )}
    </div>
  );
}
